import { NewCalendarIcon } from '@commenda/icons/new/calendar';
import { cn } from '@commenda/utils';
import { useState } from 'react';
import { Button } from '../Button';
import { IconButton } from '../IconButton';
import { Icons } from '../Icons';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export interface MonthPickerProps {
  /** Selected month (0-indexed) */
  month: number;
  /** Selected year */
  year: number;
  onChange: (month: number, year: number) => void;
  /** Trigger override, merged after the defaults. */
  className?: string;
}

export function MonthPicker({
  month,
  year,
  onChange,
  className,
}: MonthPickerProps) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(year);

  const label = `${MONTHS[month]} ${year}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            'flex h-10 w-40 box-border items-center justify-between rounded-md px-xl py-2.5 bg-bg-primary txt-small-regular text-text-primary gap-2 whitespace-nowrap',
            'border border-border-primary hover:border-border-primary-hover transition-colors',
            'appearance-none outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-bg-brand-hover)] focus-visible:border-fg-brand cursor-pointer',
            open &&
              'ring-4 ring-[var(--color-bg-brand-hover)] border-fg-brand hover:border-fg-brand',
            className
          )}
        >
          <span>{label}</span>
          <NewCalendarIcon className="size-4 shrink-0 text-fg-quaternary" />
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} className="w-[260px] p-0">
        <div className="rounded-xl border border-border-primary bg-bg-primary shadow-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-primary">
            <IconButton
              icon={<Icons.ChevronLeft className="size-4" />}
              size="sm"
              aria-label="Previous year"
              onClick={() => setViewYear((y) => y - 1)}
            />
            <span className="txt-small-bold text-text-primary">{viewYear}</span>
            <IconButton
              icon={<Icons.ChevronRight className="size-4" />}
              size="sm"
              aria-label="Next year"
              onClick={() => setViewYear((y) => y + 1)}
            />
          </div>
          <div className="grid grid-cols-3 gap-1 p-3">
            {MONTHS.map((m, i) => {
              const isSelected = i === month && viewYear === year;
              return (
                <Button
                  key={m}
                  variant={isSelected ? 'primary' : 'secondary'}
                  size="sm"
                  className="w-full border-none"
                  onClick={() => {
                    onChange(i, viewYear);
                    setOpen(false);
                  }}
                >
                  {m}
                </Button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
